import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { GetAllModulesResponse, GetUserPermissionsResponse, UpdatePermissionsRequest } from '@models/permissions.model';
import { firstValueFrom, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class ModulesService {

  private url = environment.apiUrl;
  private http = inject(HttpClient)

  /**
   * @name getAllModules
   * @description Obtiene todos los módulos disponibles.
   * @returns {Promise<GetAllModulesResponse>} Una promesa que resuelve con la lista de todos los módulos.
   */
  public async getAllModules(): Promise<GetAllModulesResponse> {
    return firstValueFrom(this.http.get<GetAllModulesResponse>(`${this.url}/catalogs/modules`));
  }

  /**
   * @name getUserPermissionsByUUID
   * @description Obtiene los permisos de un usuario específico mediante su UUID.
   * @param {string} uuid El UUID del usuario.
   * @returns {Promise<GetUserPermissionsResponse[]>} Una promesa que resuelve con una lista de los permisos del usuario.
   */
  public async getUserPermissionsByUUID(uuid: string): Promise<GetUserPermissionsResponse[]> {
    return firstValueFrom(this.http.get<GetUserPermissionsResponse[]>(`${this.url}/access/permissions/${uuid}`));
  }

  /**
   * @name getUserPermissionsByUUIDOBS
   * @description Obtiene los permisos de un usuario específico mediante su UUID como un Observable.
   * @param {string} uuid El UUID del usuario.
   * @returns {Observable<GetUserPermissionsResponse[]>} Un observable que emite una lista de los permisos del usuario.
   */
  public getUserPermissionsByUUIDOBS(uuid: string): Observable<GetUserPermissionsResponse[]> {
    return this.http.get<GetUserPermissionsResponse[]>(`${this.url}/access/permissions/${uuid}`);
  }

  /**
   * @name updatePermissionsByUUID
   * @description Actualiza los permisos de un usuario específico mediante su UUID.
   * @param {string} uuid El UUID del usuario.
   * @param {UpdatePermissionsRequest[]} body Un arreglo con los permisos actualizados.
   * @returns {Promise<any>} Una promesa que resuelve con la respuesta de la actualización.
   */
  public async updatePermissionsByUUID(uuid: string, body: UpdatePermissionsRequest[]): Promise<any> {
    return firstValueFrom(this.http.patch(`${this.url}/access/permissions/${uuid}`, body));
  }

  /**
   * @name getModulePermissions
   * @description Obtiene los permisos de un módulo específico dentro de una lista de permisos de usuario.
   * @param {string} name El nombre del módulo.
   * @param {GetUserPermissionsResponse[]} data La lista de permisos del usuario.
   * @returns {Permissions | any} Los permisos para el módulo especificado, o `any` si no se encuentran. (Nota: 'Permissions' debe ser un tipo definido o reemplazar por 'any')
   */
  public getModulePermissions(name: string, data: GetUserPermissionsResponse[]): Permissions | any {
    return data.find((module: GetUserPermissionsResponse) => module.module_name === name)?.permissions;
  }
}
